import {Database, useDataQuery, useViewQuery} from '@tnc/shared';
import clsx from 'clsx';
import {useCallback, useEffect, useState} from 'react';

import {brandT} from '@/lib/types/brand.types';

import {usdFormatter} from '@/lib/helpers';

import {
  MarketPositionPrepT,
  useCreateMarketPositions,
} from '@/pages/(shared)/positions/new/providers/createMarketPositionsProvider';

import {useMarkets} from '@/providers/components/marketsProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {SearchAndSelectComponent} from '@/components';

type MarketPositionRowPropsT = {
  position: MarketPositionPrepT;
  onUpdatePosition: (data: MarketPositionPrepT) => void;
  onDelete: () => void;
  onDuplicatePosition: (data: MarketPositionPrepT) => void;
  index: number;
};

/**
 * Market position row component
 * @param {MarketPositionRowProps} onDelete - The function to call when the row is deleted
 * @returns {JSX.Element} - The form row component
 */
function MarketPositionRow({
  onDelete,
  onDuplicatePosition,
  position,
  onUpdatePosition,
  index,
}: MarketPositionRowPropsT) {
  const {toast} = useNotifications();
  const {getDistributorIdsForMarket} = useMarkets();
  const {getRemainingQuantities} = useCreateMarketPositions();

  // result states
  const [distributorIds, setDistributorIds] = useState<number[]>([]);

  // search states
  const [showMarketSearch, setShowMarketSearch] = useState<boolean>(false);
  const [showBrandSearch, setShowBrandSearch] = useState<boolean>(false);
  const [showProductSearch, setShowProductSearch] = useState<boolean>(false);

  // search terms
  const [marketSearchTerm, setMarketSearchTerm] = useState<string>('');
  const [brandSearchTerm, setBrandSearchTerm] = useState<string>('');
  const [productSearchTerm, setProductSearchTerm] = useState<string>('');

  const remainingQuantities = getRemainingQuantities(
    position.product?.product_id || null,
    position.market?.market_id || null,
    index
  );

  const {data: {data: markets} = {data: []}} = useDataQuery({
    table: 'Market',
    columns: ['market_id', 'market_name'],
    searchTerm: marketSearchTerm,
    searchColumn: 'market_name',
    enabled: showMarketSearch,
  });

  const {data: {data: brands} = {data: []}} = useDataQuery({
    table: 'Distributor_Brand',
    columns: [
      'distributor_brand_id',
      'fk_brand_id',
      'fk_distributor_id',
      ['Brand', ['brand_id', 'brand_name', 'brand_logo_url']],
    ],
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'in',
        value: distributorIds,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    searchTerm: brandSearchTerm,
    searchColumn: 'Brand.brand_name',
    enabled: !!position.market?.market_id,
  });

  const {data: {data: availableItems} = {data: []}} = useViewQuery({
    view: 'market_available_items',
    single: false,
    filters: [
      {
        column: 'market_id',
        operator: 'eq',
        value: position.market?.market_id || null,
      },
      {
        column: 'brand_id',
        operator: 'eq',
        value: position.brand?.brand_id || null,
      },
    ],
    orderBy: {
      column: 'market_price',
      ascending: true,
    },
    searchColumn: 'product_name',
    searchTerm: productSearchTerm,
    enabled: !!position.market?.market_id && !!position.brand?.brand_id,
  });

  /**
   * Gets the distributor ids for a selected market to be used for brand search
   */
  const getDistributorIds = useCallback(
    async (marketId: number) => {
      if (!marketId) {
        return;
      }
      const distributorIds = await getDistributorIdsForMarket(marketId);

      setDistributorIds(distributorIds || []);
    },
    [getDistributorIdsForMarket]
  );

  useEffect(() => {
    if (position.market?.market_id) {
      getDistributorIds(position.market.market_id);
    }
  }, [position.market?.market_id, getDistributorIds]);

  /**
   * Handles the market select event - updates the market state
   * @param {marketT} item - The market to select
   */
  const handleMarketSelect = async (item: {market_id: number; market_name: string}) => {
    const new_position: MarketPositionPrepT = {
      ...position,
      market: item,
      brand: null,
      product: null,
      quantities: {
        retail_quantity: 0,
        distributor_quantity: 0,
        pending_quantity: 0,
      },
      max_quantities: {
        max_retail_quantity: 0,
        max_distributor_quantity: 0,
        max_pending_quantity: 0,
      },
      price: 0,
      markup: 0,
      error: false,
      errorMessage: '',
    };

    onUpdatePosition(new_position);
    setShowMarketSearch(false);
  };

  /**
   * Handles the brand select event - updates the brand state
   * @param {brandT} item - The brand to select
   */
  const handleBrandSelect = (item: brandT) => {
    const new_position = {
      ...position,
      brand: item,
      product: null,
      quantities: {
        retail_quantity: 0,
        distributor_quantity: 0,
        pending_quantity: 0,
      },
      max_quantities: {
        max_retail_quantity: 0,
        max_distributor_quantity: 0,
        max_pending_quantity: 0,
      },
      price: 0,
      markup: 0,
      error: false,
      errorMessage: '',
    };

    onUpdatePosition(new_position);
    setShowBrandSearch(false);
  };

  /**
   * Handles the product select event - updates the product state
   * @param {productT} item - The product to select
   */
  const handleProductSelect = (item: Database['public']['Views']['market_available_items']['Row']) => {
    if (!item.product_id) return;
    if (!item.market_price || !item.factored_price) {
      toast.error('This is an invalid item. Try again.');
      return;
    }

    onUpdatePosition({
      ...position,
      original_position_id: item.position_id,
      product: {
        product_id: item.product_id,
        product_name: item.product_name,
        product_logo: item.product_logo,
      },
      price: item.market_price,
      initial_wholesale_price: item.market_price,
      factored_price: item.factored_price,
      quantities: {
        retail_quantity: 0,
        distributor_quantity: 0,
        pending_quantity: 0,
      },
      max_quantities: {
        max_retail_quantity: item.retail_quantity || 0,
        max_distributor_quantity: item.distribution_quantity || 0,
        max_pending_quantity: item.pending_quantity || 0,
      },
      remaining_quantities: {
        retail: item.retail_quantity || 0,
        distributor: item.distribution_quantity || 0,
        pending: item.pending_quantity || 0,
      },
    });
    setShowProductSearch(false);
  };

  /**
   * Handles the markup change event - updates the markup state
   * Ensures markup doesn't make final price exceed factored_price
   * @param {React.ChangeEvent<HTMLInputElement>} e - The event object
   */
  const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const curPrice = position.price;
    const factoredPrice = position.factored_price;

    // Get requested markup from input
    const requestedMarkup = Math.max(0, parseFloat(e.target.value));

    // Calculate max allowed markup to not exceed factored price
    const maxAllowedMarkup = factoredPrice - curPrice;

    // Limit markup to not exceed factored price
    const newMarkup = Math.min(requestedMarkup, maxAllowedMarkup);

    const new_position = {
      ...position,
      markup: newMarkup,
    };

    onUpdatePosition(new_position);
  };

  return (
    <>
      {position.error && (
        <tr>
          <td colSpan={10}>
            <div className="alert alert-danger" role="alert">
              {position.errorMessage}
            </div>
          </td>
        </tr>
      )}
      <tr
        className={clsx(
          'border border-defaultborder dark:border-defaultborder/10',
          position.error && 'outline outline-1 outline-red'
        )}
      >
        {/* MARKET */}
        <td>
          <div className="flex items-center">
            {position.market ? (
              <span className="mr-2">{position.market.market_name}</span>
            ) : (
              <span className="mr-2 text-gray-400">No market selected</span>
            )}
            <button
              aria-label="search"
              className="ti-btn ti-btn-sm ti-btn-light"
              onClick={() => {
                setShowMarketSearch(true);
              }}
            >
              <i className="ri-search-line"></i>
            </button>
          </div>

          <SearchAndSelectComponent
            title="Search and Select Market"
            isVisible={showMarketSearch}
            onClose={() => {
              setShowMarketSearch(false);
            }}
            onSelect={item => {
              handleMarketSelect(item);
            }}
            onSearch={(idx: string) => {
              setMarketSearchTerm(idx);
            }}
            results={markets || []}
            mapStructure={{
              displayFields: [
                {
                  title: 'Market Name',
                  field: 'market_name',
                },
              ],
              valueField: 'market_id',
            }}
          />
        </td>
        {/* BRAND */}
        <td>
          <div className="flex items-center">
            {position.brand ? (
              <span className="mr-2">{position.brand.brand_name}</span>
            ) : (
              <span className="mr-2 text-gray-400">No brand selected</span>
            )}
            <button
              aria-label="search"
              className="ti-btn ti-btn-sm ti-btn-light"
              onClick={() => {
                if (!position.market) {
                  toast.error('Please select a market first');
                } else {
                  setShowBrandSearch(true);
                }
              }}
            >
              <i className="ri-search-line"></i>
            </button>
          </div>

          <SearchAndSelectComponent
            title="Search and Select Brand"
            isVisible={showBrandSearch}
            onClose={() => {
              setShowBrandSearch(false);
            }}
            onSelect={(item: brandT) => {
              handleBrandSelect(item);
            }}
            onSearch={(idx: string) => {
              setBrandSearchTerm(idx);
            }}
            results={brands.map(brand => brand.Brand)}
            mapStructure={{
              displayFields: [
                {
                  isImage: true,
                  title: 'Brand Logo',
                  field: 'brand_logo_url',
                },
                {
                  title: 'Brand Name',
                  field: 'brand_name',
                },
              ],
              valueField: 'brand_id',
            }}
          />
        </td>
        {/* PRODUCT */}
        <td>
          <div className="flex items-center">
            {position.product ? (
              <>
                {position.product.product_logo && (
                  <img
                    src={position.product.product_logo}
                    alt="Product"
                    className="h-12 w-12 flex-shrink-0 rounded-md object-cover"
                  />
                )}
                <span className="mr-2 max-w-[15rem] truncate">{position.product.product_name}</span>
              </>
            ) : (
              <span className="mr-2 text-gray-400">No product selected</span>
            )}
            <button
              aria-label="search"
              className="ti-btn ti-btn-sm ti-btn-light"
              onClick={() => {
                if (!position.brand) {
                  toast.error('Please select a brand first');
                } else {
                  setShowProductSearch(true);
                }
              }}
            >
              <i className="ri-search-line"></i>
            </button>
          </div>

          <SearchAndSelectComponent
            title="Search and Select Product"
            isVisible={showProductSearch}
            onClose={() => {
              setShowProductSearch(false);
            }}
            onSearch={idx => {
              setProductSearchTerm(idx);
            }}
            onSelect={item => {
              handleProductSelect(item);
            }}
            results={availableItems || []}
            mapStructure={{
              displayFields: [
                {
                  isImage: true,
                  title: 'Product Image',
                  field: 'product_logo',
                },
                {
                  title: 'Product Name',
                  field: 'product_name',
                },
                {
                  title: 'Market Price',
                  field: 'market_price',
                  type: 'currency',
                },
                {
                  title: 'Retail',
                  field: 'retail_quantity',
                },
                {
                  title: 'Distribution',
                  field: 'distribution_quantity',
                },
                {
                  title: 'Pending',
                  field: 'pending_quantity',
                },
              ],
              valueField: 'position_id',
            }}
          />
        </td>
        {/* RETAIL QUANTITY */}
        <td className="invoice-quantity-container relative">
          <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
            <button
              aria-label="decrease"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
              onClick={() => {
                const newValue = Math.min((position.quantities.retail_quantity || 0) - 1, remainingQuantities.retail);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    retail_quantity: newValue,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            >
              <i className="ri-subtract-line"></i>
            </button>
            <input
              type="text"
              className="form-control !w-12 border-0 !p-1 text-center"
              value={position.quantities.retail_quantity || 0}
              onChange={e => {
                const value = Math.min(Math.max(0, parseInt(e.target.value) || 0), remainingQuantities.retail);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    retail_quantity: value,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            />
            <button
              aria-label="increase"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
              onClick={() => {
                const newValue = Math.min((position.quantities.retail_quantity || 0) + 1, remainingQuantities.retail);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    retail_quantity: newValue,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
          <div className="absolute -bottom-1 left-0 right-0 text-center">
            <span className="text-xs text-gray-500">Max: {remainingQuantities.retail}</span>
          </div>
        </td>
        {/* DISTRIBUTOR QUANTITY */}
        <td className="invoice-quantity-container relative">
          <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
            <button
              aria-label="decrease"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
              onClick={() => {
                const newValue = Math.min(
                  (position.quantities.distributor_quantity || 0) - 1,
                  remainingQuantities.distributor
                );
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    distributor_quantity: newValue,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            >
              <i className="ri-subtract-line"></i>
            </button>
            <input
              type="text"
              className="form-control !w-12 border-0 !p-1 text-center"
              value={position.quantities.distributor_quantity || 0}
              onChange={e => {
                const value = Math.min(Math.max(0, parseInt(e.target.value) || 0), remainingQuantities.distributor);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    distributor_quantity: value,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            />
            <button
              aria-label="increase"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
              onClick={() => {
                const newValue = Math.min(
                  (position.quantities.distributor_quantity || 0) + 1,
                  remainingQuantities.distributor
                );
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    distributor_quantity: newValue,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
          <div className="absolute -bottom-1 left-0 right-0 text-center">
            <span className="text-xs text-gray-500">Max: {remainingQuantities.distributor}</span>
          </div>
        </td>
        {/* PENDING QUANTITY */}
        <td className="invoice-quantity-container relative">
          <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
            <button
              aria-label="decrease"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
              onClick={() => {
                const newValue = Math.min((position.quantities.pending_quantity || 0) - 1, remainingQuantities.pending);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    pending_quantity: newValue,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            >
              <i className="ri-subtract-line"></i>
            </button>
            <input
              type="text"
              className="form-control !w-12 border-0 !p-1 text-center"
              value={position.quantities.pending_quantity || 0}
              onChange={e => {
                const value = Math.min(Math.max(0, parseInt(e.target.value) || 0), remainingQuantities.pending);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    pending_quantity: value,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            />
            <button
              aria-label="increase"
              type="button"
              className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
              onClick={() => {
                const newValue = Math.min((position.quantities.pending_quantity || 0) + 1, remainingQuantities.pending);
                onUpdatePosition({
                  ...position,
                  quantities: {
                    ...position.quantities,
                    pending_quantity: newValue,
                  },
                });
              }}
              disabled={!position.product?.product_id}
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
          <div className="absolute -bottom-1 left-0 right-0 text-center">
            <span className="text-xs text-gray-500">Max: {remainingQuantities.pending}</span>
          </div>
        </td>

        {/* TOTAL COST */}
        <td>
          <span className="form-control !rounded-md !p-0">
            $
            {(
              (position.quantities.retail_quantity +
                position.quantities.distributor_quantity +
                position.quantities.pending_quantity) *
              position.price
            ).toFixed(2)}
          </span>
        </td>
        {/* MARKUP */}
        <td>
          <div className="flex items-center">
            <span className="mr-2">$</span>
            <input
              className="form-control !w-20 !rounded-md !bg-light"
              type="number"
              min="0"
              max={`${(position.factored_price - position.price).toFixed(2)}`}
              step="0.1"
              value={position.markup}
              onChange={handleMarkupChange}
            />
          </div>
        </td>
        {/* MARKET VALUE */}
        <td>
          <span className="form-control !rounded-md !p-0">{usdFormatter.format(position.price + position.markup)}</span>
        </td>
        {/* PROFIT */}
        <td>
          <span
            className="form-control !rounded-md !p-0"
            style={{
              color: 'green',
            }}
          >
            $
            {(
              (position.price + position.markup) *
                (position.quantities.retail_quantity +
                  position.quantities.distributor_quantity +
                  position.quantities.pending_quantity) -
              (position.price + position.markup) *
                (position.quantities.retail_quantity +
                  position.quantities.distributor_quantity +
                  position.quantities.pending_quantity)
            ).toFixed(2)}
          </span>
        </td>
        {/* ACTION */}
        <td className="flex items-center gap-2">
          <button
            onClick={() => onDuplicatePosition(position)}
            aria-label="copy"
            type="button"
            className="ti-btn ti-btn-sm ti-btn-icon ti-btn-light"
          >
            <i className="ri-file-copy-line"></i>
          </button>
          <button
            aria-label="delete"
            type="button"
            className="ti-btn ti-btn-sm ti-btn-icon ti-btn-danger"
            onClick={onDelete}
          >
            <i className="ri-delete-bin-5-line"></i>
          </button>
        </td>
      </tr>
    </>
  );
}

export default MarketPositionRow;
